import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Lock, Loader2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Avatar from '@/components/ui/Avatar';
import { friendsApi } from '@/api/friends';
import { secretChatsApi } from '@/api/secretChats';
import { ensureIdentityPublished } from '@/lib/secretChat/session';
import { isWebCryptoAvailable } from '@/lib/secretChat/crypto';
import { extractError } from '@/api/client';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal for starting a new 1:1 secret chat with a friend.
 *
 * Why "friends only": secret chats imply a real out-of-band trust step
 * (verifying the safety number). Forcing the picker to friends-only avoids
 * the worst kind of unsolicited "init" envelope spam from random users.
 */
export default function CreateSecretChatModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const supported = isWebCryptoAvailable();
  const [error, setError] = useState<string | null>(null);

  const { data: friends, isLoading } = useQuery({
    queryKey: ['friends'],
    queryFn: () => friendsApi.list(),
    enabled: open && supported,
  });

  const mutation = useMutation({
    mutationFn: async (peerId: string) => {
      // Make sure our keys are on the server before we try to derive a session.
      await ensureIdentityPublished();
      return secretChatsApi.create(peerId);
    },
    onSuccess: (chat) => {
      onClose();
      navigate(`/secret/${chat.id}`);
    },
    onError: (err) => setError(extractError(err)),
  });

  return (
    <Modal open={open} onClose={onClose} title="Новый тунель">
      {!supported ? (
        <div className="text-sm text-surface-300 space-y-3">
          <p className="text-red-400 font-medium">Браузер не поддерживается.</p>
          <p>
            Тунели требуют WebCrypto и IndexedDB. Откройте сайт в современном
            браузере (Chrome, Firefox, Safari, Edge) через HTTPS или localhost.
          </p>
          <button onClick={onClose} className="btn-ghost w-full">Закрыть</button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start gap-2 bg-surface-800/60 border border-surface-700 rounded-lg p-3">
            <Lock size={14} className="text-brand-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-surface-400 leading-relaxed">
              Сообщения шифруются на вашем устройстве и расшифровываются только у собеседника.
              Сервер хранит лишь непрозрачные конверты. Историю нельзя восстановить, если вы
              потеряете доступ к устройству.
            </p>
          </div>

          {error && (
            <div className="bg-red-900/40 border border-red-700 text-red-300 text-xs rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-6 text-surface-500 text-sm">
              <Loader2 size={16} className="animate-spin mr-2" />
              Загрузка друзей...
            </div>
          )}

          {friends && friends.length === 0 && (
            <p className="text-surface-500 text-sm text-center py-4">
              Сначала добавьте кого-нибудь в друзья.
            </p>
          )}

          <div className="space-y-1 max-h-72 overflow-y-auto">
            {friends?.map((f) => (
              <button
                key={f.friendshipId}
                disabled={mutation.isPending}
                onClick={() => { setError(null); mutation.mutate(f.friend.id); }}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-surface-800 disabled:opacity-50 transition-colors text-left"
              >
                <Avatar src={f.friend.avatarUrl} username={f.friend.username} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-surface-100 text-sm truncate">{f.friend.displayName}</p>
                  <p className="text-surface-500 text-xs font-mono truncate">@{f.friend.username}</p>
                </div>
                {mutation.isPending && mutation.variables === f.friend.id && (
                  <Loader2 size={14} className="animate-spin text-brand-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
